import React, { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import confetti from 'canvas-confetti';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import {
  XMarkIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';

import LoadingSpinner from '../../shared/spinner';
import { formatTimeRemaining } from '../../utils';
import {
  PersonaCandidateResponse,
  PersonaSessionResponse
} from '../../api/types';
import {
  useCreatePersona,
  useGetUser,
  useGetPersonaProfiles
} from '../../hooks/api-hooks';

import ProfilePickerRelaxed from './profile-picker-relaxed';

interface ModalProps {
  candidateUrl: string | null;
  candidateId: string | null;
  targetUrl: string | null;
  isOpen: boolean;
  similarity: number | null;
  setOpen: (isOpen: boolean) => void;
  previewNeoface: (
    personaSessionTargetUrl: string,
    candidateId: string,
    personaCandidateSwapUrl: string,
    similarity: number
  ) => void;
  personaSession: PersonaSessionResponse | undefined;
  candidates: PersonaCandidateResponse[] | undefined;
  isFirstPersona: boolean;
  setIsFirstPersona: (val: boolean) => void;
  setNeofaceSelectedModalOpen: (open: boolean) => void;
}

export default function NeofaceCompare({
   candidateUrl,
   candidateId,
   targetUrl,
   isOpen,
   similarity,
   setOpen,
   previewNeoface,
   candidates,
   personaSession,
   isFirstPersona,
   setIsFirstPersona,
   setNeofaceSelectedModalOpen
 }: ModalProps) {
  const [showOriginal, setShowOriginal] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(true);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [showUrgentNotice, setShowUrgentNotice] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [deletedTime, setDeletedTime] = useState<Date | null>(null);

  // For selecting which profile to assign this new face to:
  const profilesQuery = useGetPersonaProfiles();
  const profiles = profilesQuery.data || [];
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');

  const chooseMutation = useCreatePersona();
  const user = useGetUser();

  // On open, reset any local states
  useEffect(() => {
    if (!isOpen) {
      setShowOriginal(false);
      setShowTooltip(true);
    } else {
      // Whenever the modal opens, default to "primary" (or first) profile
      if (profiles.length > 0) {
        const primaryProfile =
          profiles.find((p) => p.type === 'primary') || profiles[0];
        if (primaryProfile) {
          setSelectedProfileId(primaryProfile.id);
        }
      }
    }
  }, [isOpen, profiles]);

  // Timer logic
  useEffect(() => {
    const updateTimer = () => {
      if (!personaSession?.createdAt) return;

      if (personaSession.targetFaceDeletedAt) {
        setTimeRemaining('Deleted');
        setIsExpired(true);
        setShowOriginal(false);
        setDeletedTime(new Date(personaSession.targetFaceDeletedAt));
        return;
      }

      const expiresIn = new Date(personaSession.targetFaceDeletesIn).getTime();
      const now = new Date().getTime();
      const remaining = expiresIn - now;

      if (remaining <= 0) {
        setTimeRemaining('Deleted');
        setIsExpired(true);
        setShowOriginal(false);
        return;
      }

      setTimeRemaining(formatTimeRemaining(remaining));
      setShowUrgentNotice(remaining < 5 * 60 * 1000); // < 5 min
    };

    updateTimer();
    const timer = setInterval(updateTimer, 60000);
    return () => clearInterval(timer);
  }, [personaSession]);

  const navigateBetweenNeofaces = (move: number) => {
    if (!candidates) return;
    const currentIndex = candidates.findIndex((c) => c.id === candidateId);
    if (currentIndex < 0) return;

    const nextIndex = currentIndex + move;
    if (nextIndex >= 0 && nextIndex < candidates.length) {
      const nextCandidate = candidates[nextIndex];
      previewNeoface(
        targetUrl || '',
        nextCandidate.id,
        nextCandidate.swappedFaceUrl,
        nextCandidate.similarity
      );
      setShowOriginal(false);
    }
  };

  const getCurrentIndex = () => {
    return candidates?.findIndex((c) => c.id === candidateId) || 0;
  };

  const selectNeoface = async () => {
    if (!candidateId) return;

    // Send both candidateId + selectedProfileId
    const response = await chooseMutation.mutateAsync({
      candidateId,
      profileId: selectedProfileId,
    });

    if (response.isFirstPersona) {
      setIsFirstPersona(true);
    }
    // Confetti animation
    confetti({
      origin: { y: 0.3 },
      spread: 160,
      startVelocity: 30,
      particleCount: 200,
      angle: 90,
      zIndex: 9999,
    });
    await user.refetch();
    await profilesQuery.refetch()
    setOpen(false);
    setNeofaceSelectedModalOpen(true)
  };

  const handleImageClick = () => {
    if (!isExpired) {
      setShowOriginal(!showOriginal);
      setShowTooltip(false);
    }
  };

  const formatDeletedDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={() => setOpen(false)} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

      <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
        <DialogPanel className="relative w-full max-w-4xl rounded-lg bg-white shadow-xl">
          {/* Close button */}
          <button
            onClick={() => setOpen(false)}
            className="absolute right-2 top-2 z-10 rounded-full bg-white/80 p-1.5 text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="relative flex items-center bg-black">
            <button
              onClick={() => navigateBetweenNeofaces(-1)}
              className="absolute left-2 z-10 rounded-full bg-white/80 p-1.5 shadow-lg backdrop-blur-sm hover:bg-white"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
            </button>

            <div className="relative w-full">
              {/* Toggling between Original & Anonymized */}
              <div
                className={`${isExpired ? 'cursor-default' : 'cursor-pointer'}`}
                onClick={handleImageClick}
              >
                <img
                  src={
                    showOriginal && !isExpired
                      ? targetUrl || ''
                      : candidateUrl || ''
                  }
                  alt="Preview your neoface"
                  className="mx-auto max-h-[70vh] w-auto object-contain"
                />

                {/* Status labels: time left, etc. */}
                <div className="absolute left-0 top-0 z-10 p-2 sm:p-4 flex flex-col gap-2">
                  {/* Timer Badge or Deletion Confirmation */}
                  {isExpired ? (
                    <div className="self-start rounded-full px-3 py-1.5 text-sm font-medium bg-green-600/90 text-white backdrop-blur-sm">
                      <div className="flex items-center gap-1.5">
                        <ShieldCheckIcon className="h-4 w-4" />
                        Original face deleted
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`self-start rounded-full px-3 py-1.5 text-sm font-medium backdrop-blur-sm transition-colors
                        ${
                        showUrgentNotice
                          ? 'bg-red-500/90 text-white'
                          : 'bg-black/70 text-white'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        {showUrgentNotice ? (
                          <ExclamationCircleIcon className="h-4 w-4" />
                        ) : (
                          <ClockIcon className="h-4 w-4" />
                        )}
                        {showUrgentNotice
                          ? `Original deletes in ${timeRemaining}`
                          : `Original deletes in ${timeRemaining}`}
                      </div>
                    </div>
                  )}

                  {/* Anonymized/Original label */}
                  <div className="self-start rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm sm:text-sm">
                    {showOriginal && !isExpired ? 'Original' : 'Anonymized'}
                  </div>
                </div>

                {/* Center tooltip */}
                {showTooltip && !isExpired && (
                  <div className="absolute text-center left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-black px-4 py-2 text-sm font-medium text-white">
                    <span className="hidden sm:inline">Tap image to compare</span>
                    <div className="sm:hidden">
                      <div>Tap image to</div>
                      <div>compare</div>
                    </div>
                  </div>
                )}

                {/* Deletion confirmation notice (when original is deleted) */}
                {isExpired && (
                  <div className="absolute left-0 bottom-12 z-10 p-2 sm:p-4 w-full">
                    <div className="bg-black/70 text-white text-sm p-2 sm:p-3 rounded-lg backdrop-blur-sm">
                      <div className="flex items-start">
                        <TrashIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Original face permanently deleted</p>
                          {deletedTime && (
                            <p className="text-xs text-gray-300 mt-1">Deleted on {formatDeletedDate(deletedTime)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Index pagination */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm sm:text-sm">
                {getCurrentIndex() + 1} / {candidates?.length}
              </div>
            </div>

            <button
              onClick={() => navigateBetweenNeofaces(1)}
              className="absolute right-2 z-10 rounded-full bg-white/80 p-1.5 shadow-lg backdrop-blur-sm hover:bg-white"
            >
              <ArrowRightIcon className="h-5 w-5 text-gray-700" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 p-3">
            <div className="flex flex-col items-center gap-2">
              <>
                {profiles.length > 1 ? (
                  <div className="text-sm text-gray-700 mb-2">
                    <span className="mr-2">Assign to:</span>
                    <ProfilePickerRelaxed
                      profiles={profiles}
                      selectedProfileId={selectedProfileId}
                      onChange={(id) => setSelectedProfileId(id)}
                      placeholder="Select Profile"
                    />
                  </div>
                ) : (
                  profiles.length === 1 && (
                    <p className="text-sm text-gray-700 mb-2">
                      Assigning to: <strong>{profiles[0].displayName}</strong>
                    </p>
                  )
                )}

                <button
                  onClick={selectNeoface}
                  disabled={chooseMutation.isPending || !selectedProfileId}
                  className="flex rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:bg-indigo-400"
                >
                  {chooseMutation.isPending ? (
                    <div className="flex items-center justify-center gap-2">
                      <LoadingSpinner className="h-5 w-5" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    'Select'
                  )}
                </button>
              </>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}